import { QueryFactory } from '@lmt/shared-types';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { OptionList } from '@lmt/unicorn-shared/app/components/common/ui-kit/OptionList';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus.svg?react';
import { useTriggerOnClickOutsideElement } from '@lmt/unicorn-shared/app/helpers/useTriggerOnClickOutsideElement';
import queryString from 'query-string';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AddTabButton.module.scss';
import { useEventCallback } from '@mui/material';

interface TabInfo {
  title: string;
  page: string;
  search?: string;
}

export const AddTabButton: React.FC = () => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const optionListRef = useRef<HTMLDivElement>(null);
  const hide = useEventCallback(() => setExpanded(false));
  useTriggerOnClickOutsideElement(optionListRef, hide, expanded);
  const claims = useClaims();
  const tabs = useTabs();
  const i18n = useTranslation();

  const { data: calendarConfigurations } =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationsQuery({
      throwOnError: false,
    });

  const { data: patientListConfigurations } =
    QueryFactory.PatientListConfigurationQuery.useGetPatientListConfigurationsQuery(
      {
        throwOnError: false,
      },
    );

  const pinnedCalendarConfigurationTabs: TabInfo[] = useMemo(() => {
    return (calendarConfigurations ?? [])
      .filter((x) => x.isPinned)
      .map((x) => ({
        title: i18n.t(x.name, { defaultValue: x.name }),
        page: pages.calendar.link(),
        search: queryString.stringify({ configurationId: x.id }),
      }));
  }, [calendarConfigurations]);

  const pinnedPatientListConfigurationTabs: TabInfo[] = useMemo(() => {
    return (patientListConfigurations ?? [])
      .filter((x) => x.isPinned)
      .map((x) => ({
        title: i18n.t(x.name, { defaultValue: x.name }),
        page: pages.patients.link(),
        search: queryString.stringify({ configurationId: x.id }),
      }));
  }, [patientListConfigurations]);

  const developerTabs: TabInfo[] = useMemo(() => {
    return claims.permissions.developerMode
      ? [
          {
            title: i18n.t('tabs.settings'),
            page: pages.settings.link(),
          },
          {
            title: 'UI Editor',
            page: pages.dataBlockSettings.link(),
          },
        ]
      : [];
  }, []);

  const openPage = useEventCallback((pageInfo: TabInfo) => {
    tabs.openTab(pageInfo.page, pageInfo.search);
  });

  if (
    !calendarConfigurations
      ?.concat(patientListConfigurations ?? [])
      ?.some((x) => x.isPinned) &&
    !claims.permissions.developerMode
  ) {
    return null;
  }

  return (
    <div>
      {/* wrapper div is required for OptionList to be displayed properly */}
      <IconButton
        className={styles.icon}
        onClick={() => setExpanded(!expanded)}
        iconComponent={<AddIcon className={styles.formButtonIcon} />}
        testId={'navPanel-addTab'}
      />
      <OptionList<TabInfo>
        visible={expanded}
        options={[
          ...pinnedCalendarConfigurationTabs,
          ...pinnedPatientListConfigurationTabs,
          ...developerTabs,
        ]}
        onClick={(option) => {
          openPage(option);
          setExpanded(false);
        }}
        labelField="title"
        className={styles.optionListContainer}
        elementRef={optionListRef}
      />
    </div>
  );
};
